import {call, put, select, take, takeLatest} from 'redux-saga/effects';
import * as Api from '../api';
import { AppAction, AppState } from '../../../Store/types';
import * as Routines from "../routines";
import { feedbackFailure } from '../../../Feedback/reducer';
import { Battery, Position, RootState, Signal, Transaction } from '../types';
import { getLocations, prepareTransaction } from '../../Log/utils';
import {AlarmFilterEnum} from "@Safemate/DeviceList/AlarmFilterEnum";
import { IDevice } from '@Safemate/Model/Device';
import { selectDevices, selectFilterSelector, selectFilteredDevices } from '../selectors';

export interface GetAllTransactions{
  maxTransactionId: number;
  transactionCount: number;
  deviceIds?: number[]
}
export interface GetTransactions{
  deviceId: number;
  transactionCount?: number;
  maxTransactionId?: number;
  filter?: boolean;
}

export interface GetTransactionsResult{
  transactions: Transaction[];
  maxTransactionId: number;
  replace?: boolean;
  ignoreFilter?: boolean;
}

const maxTransactionIdSelector = (state: AppState) => state.transactionLog.transaction.maxTransactionId; 
const transactionCountSelector = (state: AppState) => state.transactionLog.transaction.transactionCount; 
const positionCountSelector = (state: AppState) => state.transactionLog.transaction.positionCount;
const transactionsSelector = (state: AppState) => state.transactionLog.transaction.transactions;
const renderSelector = (state: AppState) => state.transactionLog.transaction.render;
const googleMapsSelector = (state: AppState) => state.transactionLog.transaction.googleMaps;

function* getTransactionsForAll(action: AppAction<GetAllTransactions>){
  try{
    const filter: AlarmFilterEnum[] = yield select(selectFilterSelector);

    let deviceIds = null;
    const filteredDevices: IDevice[] = yield select(selectFilteredDevices);

    if(filter.length > 0){
      deviceIds = filteredDevices.map(device => device.deviId);
    }

    const maxTransactionId: number = yield select(maxTransactionIdSelector);
    let transactionCount: number = yield select(transactionCountSelector);
    console.log(transactionCount, action.payload);

    transactionCount += action.payload.transactionCount;

    const transactions: Transaction[] = yield call(Api.getAllTransactions, {
      transactionCount,
      maxTransactionId: action.payload.maxTransactionId !== undefined ? action.payload.maxTransactionId : maxTransactionId,
      deviceIds
    })

    yield put(Routines.getTransactionsForAll.success({transactionCount, noMore: transactions.length === 0}));
    yield modifyTransactions(transactions, maxTransactionId, false, false, false, filteredDevices);
  }
  catch(e){
    console.log(e);
    yield feedbackFailure(e);
    yield put(Routines.getTransactionsForAll.failure());
  }
}


function* modifyTransactions(transactions: Transaction[], maxTransactionId: number, replace: boolean, ignoreFilter: boolean, getMoreTransactions: boolean, devices: IDevice[] = null){
  for(let i = 0; i < transactions.length; i++){
    prepareTransaction(transactions[i]);
    transactions[i].ignoreFilter = ignoreFilter;

    if(devices){
      const device = devices.find(device => device.deviId === transactions[i].deviId);
      transactions[i].dehaId = device ? device.dehaId : 0;
    }
  }
  const results: GetTransactionsResult = {
    transactions,
    maxTransactionId: transactions.length > 0 ? transactions[0].id : maxTransactionId,
    replace,
  }
  
  yield put(Routines.getTransactionsForAll.success(results));
  yield prepareLocations(results);
}

export interface GetPositions{
  deviceId: number;
  newDevice? : boolean;
  count?: number;
  uuid?: string;
}

export interface GetPosition{
  deviceId: number;
  tranId: number;
}

export interface GetPositionResults{
  positions: Position[];
  count: number;
  newDevice: boolean;
}

function* getPositions(action: AppAction<GetPositions>){
  try{

    const apiData: GetPositions = {
      ...action.payload,
      count: action.payload.count ? action.payload.count : yield select(positionCountSelector)
    }
  
    const positions: Position[] = yield call(Api.getPositions, apiData);

    const results:GetPositionResults = {
      positions,
      count: apiData.count,
      newDevice: action.payload.newDevice ? action.payload.newDevice : false
    }
    yield put(Routines.getPositions.success(results));
  }
  catch(e){
    yield feedbackFailure(e);
    yield put(Routines.getPositions.failure());
  }
}

function* getPositionsUuid(action: AppAction<GetPositions>){
  try{

    const apiData: GetPositions = {
      ...action.payload,
      count: action.payload.count ? action.payload.count : yield select(positionCountSelector)
    }

    const positions: Position[] = yield call(Api.getPositionsUuid, apiData);

    const results:GetPositionResults = {
      positions,
      count: apiData.count,
      newDevice: action.payload.newDevice ? action.payload.newDevice : false
    }
    yield put(Routines.getPositions.success(results));
  }
  catch(e){
    yield feedbackFailure(e);
    yield put(Routines.getPositions.failure());
  }
}

function* getPosition(action: AppAction<GetPosition>){
  try{
    const position: Position = yield call(Api.getPosition, action.payload);
    yield put(Routines.getPosition.success(position));
  }
  catch(e){
    yield feedbackFailure(e);
    yield put(Routines.getPosition.failure());
  }
}

export function* prepareLocations(data: GetTransactionsResult){
  let render = yield select(renderSelector);
  let googleMaps = yield select(googleMapsSelector);
  yield call(getLocations, data.transactions, render, googleMaps);
  yield put(Routines.getTransactionsForAll.success(data));
}

export function* updateTransaction(transactions: Transaction[]){
  yield put(Routines.getTransactionsForAll.success(transactions));
}

export function* getAddress(action: AppAction<Transaction>){
  try{
    const address = yield call(Api.getAddress, action.payload);
    yield put(Routines.getAddress.success(address));
  }
  catch(e){
    yield put(Routines.getAddress.failure());
  }
}

function* transactionSaga(){
    yield takeLatest(Routines.getTransactionsForAll.TRIGGER, getTransactionsForAll);
    yield takeLatest(Routines.getPositions.TRIGGER, getPositions);
    yield takeLatest(Routines.getPositionsUuid.TRIGGER, getPositionsUuid);
    yield takeLatest(Routines.getPosition.TRIGGER, getPosition);
}

export default transactionSaga;
